// Pictures the user shows Blip as a guide (docs/BLIP_GUIDE_PICTURES_DESIGN.md):
// added with the picture button beside Send, by pasting, or by dropping a
// file on his panel (panel.js addGuide), and drawn on the sketch pad over
// the world seen from above. Each is prepared in the page first: scaled to
// the size Claude makes use of, and encoded again, which leaves out the
// file's metadata, such as where a phone photo was taken. It goes in the
// user's message, after its words.
//
// A prepared picture: { bytes, type, width, height, thumb (a small JPEG, as
// a data URL), name, sketch (a sketch's description: { area, labels,
// strokes }, as lines), original (the file it was made from, which a TODO
// note keeps at full size: todo.js) }. The pictures the user showed in a
// chat are numbered in order across it (chatPicture), so that the user and
// Blip can refer to them, and the tools take a shown picture's number.

import { areaText } from "./world.js";

export const MESSAGE_PICTURES = 3;
const FILE_BYTES = 40 << 20;
// The most of a picture Claude makes use of: a larger one would only be
// shrunk by the API, and cost the same.
const LONG_SIDE = 1568, PIXELS = 1.15e6;
// A picture without transparency stays PNG under this, as drawings and
// screenshots usually are; a larger one, such as a photo, is a JPEG.
const PNG_BYTES = 400 << 10;
const THUMB_SIDE = 160;
// A part a model is made from straight away is at least this long on its
// long side; a smaller one is kept, to be made into a larger, clean picture
// first; one smaller than the least is refused, as too small to tell what
// it is.
export const CROP_SIDE = 256, CROP_LEAST = 48;
export const CANNOT_OPEN = "Blip can't open that file. Try a PNG or JPEG.";

export class GuideError extends Error {}

// A file's name as the message names it: no quotes or line breaks.
const cleanName = (name) => String(name || "").replace(/[\u0000-\u001f"<>]/g, "").trim().slice(0, 60) || "picture";

// A picture drawn at a size, halving first, which keeps a large picture
// sharp when it is made small.
function drawScaled(source, width, height) {
  let current = source, currentWidth = source.width, currentHeight = source.height;
  while (currentWidth / 2 >= width && currentHeight / 2 >= height) {
    const half = new OffscreenCanvas(Math.round(currentWidth / 2), Math.round(currentHeight / 2));
    const context = half.getContext("2d");
    context.imageSmoothingQuality = "high";
    context.drawImage(current, 0, 0, half.width, half.height);
    current = half;
    currentWidth = half.width;
    currentHeight = half.height;
  }
  const canvas = new OffscreenCanvas(width, height);
  const context = canvas.getContext("2d");
  context.imageSmoothingQuality = "high";
  context.drawImage(current, 0, 0, width, height);
  return canvas;
}

const dataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

// A small JPEG of a picture, on white, as a data URL.
async function thumbnail(source) {
  const scale = Math.min(1, THUMB_SIDE / Math.max(source.width, source.height));
  const width = Math.max(1, Math.round(source.width * scale)), height = Math.max(1, Math.round(source.height * scale));
  const small = drawScaled(source, width, height);
  const canvas = new OffscreenCanvas(width, height);
  const context = canvas.getContext("2d");
  context.fillStyle = "#fff";
  context.fillRect(0, 0, width, height);
  context.drawImage(small, 0, 0);
  return dataUrl(await canvas.convertToBlob({ type: "image/jpeg", quality: 0.8 }));
}

// A picture's pixels encoded again: PNG with transparency, PNG under 400 KB
// without, otherwise JPEG at quality 0.85.
async function encode(canvas, alpha) {
  let transparent = false;
  if (alpha) {
    const pixels = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height).data;
    for (let at = 3; at < pixels.length && !transparent; at += 4) transparent = pixels[at] < 255;
  }
  let blob = await canvas.convertToBlob({ type: "image/png" });
  if (!transparent && blob.size > PNG_BYTES) blob = await canvas.convertToBlob({ type: "image/jpeg", quality: 0.85 });
  return { bytes: new Uint8Array(await blob.arrayBuffer()), type: blob.type };
}

// The size a picture is sent at: at most 1568 pixels on its long side and
// 1.15 million pixels in all.
export function guideSize(width, height) {
  const scale = Math.min(1, LONG_SIDE / Math.max(width, height), Math.sqrt(PIXELS / (width * height)));
  return { width: Math.max(1, Math.floor(width * scale)), height: Math.max(1, Math.floor(height * scale)) };
}

// A picture file (a File or Blob) prepared to be shown to Blip. Throws a
// GuideError that says what to tell the user.
export async function prepareGuide(file, name = file.name) {
  if (file.size > FILE_BYTES) throw new GuideError("That picture is too large: Blip takes pictures of up to 40 MB.");
  let image;
  try {
    // The right way up, as a phone took it.
    image = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new GuideError(CANNOT_OPEN);
  }
  try {
    if (!image.width || !image.height) throw new GuideError(CANNOT_OPEN);
    const size = guideSize(image.width, image.height);
    const canvas = drawScaled(image, size.width, size.height);
    const { bytes, type } = await encode(canvas, file.type !== "image/jpeg");
    return { bytes, type, width: size.width, height: size.height, thumb: await thumbnail(canvas), name: cleanName(name),
      original: file };
  } finally {
    image.close();
  }
}

// A drawn picture, such as a sketch on the world, prepared as a file is.
export async function prepareDrawing(source, name, sketch = null) {
  const size = guideSize(source.width, source.height);
  const canvas = drawScaled(source, size.width, size.height);
  const { bytes, type } = await encode(canvas, false);
  return { bytes, type, width: size.width, height: size.height, thumb: await thumbnail(canvas), name: cleanName(name), sketch };
}

// The image block of a prepared picture, as the API takes it: standard
// base64, padded with =.
export function imageBlock(picture) {
  let binary = "";
  for (let at = 0; at < picture.bytes.length; at += 0x8000) {
    binary += String.fromCharCode(...picture.bytes.subarray(at, at + 0x8000));
  }
  return { type: "image", source: { type: "base64", media_type: picture.type, data: btoa(binary) } };
}

// What the message says of its pictures, numbered from first: "Picture 3
// (photo.jpg, 1236 x 927) is attached.", and for a sketch, where its marks
// are in the world.
export function pictureLines(pictures, first) {
  return pictures.map((picture, index) => {
    const n = first + index;
    if (!picture.sketch) return `Picture ${n} (${picture.name}, ${picture.width} x ${picture.height}) is attached.`;
    const { area, labels, strokes } = picture.sketch;
    const lines = [labels.length || strokes.length ? `Picture ${n} is the user's sketch over the world seen from straight above:` :
      `Picture ${n} is the world seen from straight above, with nothing drawn on it:`, `${area}.`];
    if (labels.length) lines.push("Labels, in world x,z:", ...labels);
    if (strokes.length) lines.push("Strokes, in world x,z:", ...strokes);
    return lines.join("\n");
  }).join("\n");
}

// The pictures the user showed in a chat, in order: picture n is the nth.
// Only the user's own messages hold image blocks of their own; the tools'
// pictures are inside their results.
export function shownPictures(chat) {
  const pictures = [];
  for (const message of chat ? chat.messages : []) {
    if (message.role !== "user" || !Array.isArray(message.content)) continue;
    for (const block of message.content) if (block.type === "image") pictures.push(block);
  }
  return pictures;
}

// The user's picture n of a chat: { bytes, type, width, height, name, n },
// or null. Its name and size are as the panel's log keeps them.
export function chatPicture(chat, n) {
  const block = shownPictures(chat)[n - 1];
  if (!block) return null;
  const binary = atob(block.source.data);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  let kept = null;
  for (const item of chat.log || []) {
    for (const picture of item.kind === "user" && item.pictures || []) if (picture.n === n) kept = picture;
  }
  return { bytes, type: block.source.media_type, width: kept ? kept.width : 0, height: kept ? kept.height : 0,
    name: kept && kept.name || `picture-${n}`, sketch: !!(kept && kept.sketch), n };
}

// A shown picture's number, as a tool was given it: 3, or "3" or "Picture
// 3"; null for anything else, such as a path in the project.
export function pictureNumber(value) {
  if (Number.isInteger(value)) return value >= 1 ? value : null;
  const match = typeof value === "string" && /^\s*(?:picture\s*)?(\d{1,4})\s*$/i.exec(value);
  return match ? Number(match[1]) : null;
}

// A part of a picture, [left, top, right, bottom] as fractions of its width
// and height, as a new picture of the same type: { bytes, type, width,
// height, small (under CROP_SIDE) }. Throws a GuideError for a part that is
// not one, or smaller than CROP_LEAST on its long side.
export async function cropPicture(picture, crop) {
  if (!Array.isArray(crop) || crop.length !== 4 || crop.some((value) => typeof value !== "number" || !(value >= 0 && value <= 1)) ||
      crop[0] >= crop[2] || crop[1] >= crop[3]) {
    throw new GuideError("crop is [left, top, right, bottom], each a fraction of the picture from 0 to 1, left before right " +
      "and top before bottom, such as [0.1, 0.2, 0.5, 0.9].");
  }
  const image = await createImageBitmap(new Blob([picture.bytes], { type: picture.type }));
  try {
    const left = Math.round(crop[0] * image.width), top = Math.round(crop[1] * image.height);
    const width = Math.round(crop[2] * image.width) - left, height = Math.round(crop[3] * image.height) - top;
    if (Math.max(width, height) < CROP_LEAST) {
      throw new GuideError(`That part is only ${width} x ${height} pixels, too small to tell what it is: take a larger ` +
        "part, with room round the object.");
    }
    const canvas = new OffscreenCanvas(width, height);
    canvas.getContext("2d").drawImage(image, left, top, width, height, 0, 0, width, height);
    const png = picture.type === "image/png";
    const blob = await canvas.convertToBlob(png ? { type: "image/png" } : { type: "image/jpeg", quality: 0.92 });
    return { bytes: new Uint8Array(await blob.arrayBuffer()), type: blob.type, width, height,
      small: Math.max(width, height) < CROP_SIDE };
  } finally {
    image.close();
  }
}

// ---------------------------------------------------------------- sketches

const PENS = [["red", "#e5302a"], ["blue", "#2a6be5"], ["green", "#23a046"], ["yellow", "#f2c615"]];
const PEN_WIDTH = 6;
// A stroke shorter than this, in the picture's pixels, is a dot; a label
// names a stroke this near it, as people write a word just above or below
// a line (40 pixels is about a finger's width on the pad).
const DOT_LENGTH = 4, LABEL_REACH = 40;
// A stroke whose ends are this near, or nearer than an eighth of its
// length, is a loop, when it is long enough to go round something.
const LOOP_ENDS = 24;
const MOST_POINTS = 200;
const LABEL_FONT = "bold 18px system-ui, sans-serif";

const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const pathLength = (points) => points.reduce((sum, point, index) => index ? sum + distance(points[index - 1], point) : 0, 0);

// How far a point is from a line through some points.
function distanceToPath(point, points) {
  if (points.length === 1) return distance(point, points[0]);
  let nearest = Infinity;
  for (let index = 1; index < points.length; ++index) {
    const [ax, ay] = points[index - 1], [bx, by] = points[index];
    const dx = bx - ax, dy = by - ay;
    const along = dx || dy ? Math.max(0, Math.min(1, ((point[0] - ax) * dx + (point[1] - ay) * dy) / (dx * dx + dy * dy))) : 0;
    nearest = Math.min(nearest, Math.hypot(point[0] - ax - along * dx, point[1] - ay - along * dy));
  }
  return nearest;
}

// Whether a point is inside the outline some points make.
function inside(point, points) {
  let within = false;
  for (let index = 0, last = points.length - 1; index < points.length; last = index++) {
    const [ax, ay] = points[index], [bx, by] = points[last];
    if ((ay > point[1]) !== (by > point[1]) && point[0] < (bx - ax) * (point[1] - ay) / (by - ay) + ax) within = !within;
  }
  return within;
}

// Ramer–Douglas–Peucker: the points that keep a line's shape within
// tolerance pixels.
function thin(points, tolerance) {
  if (points.length < 3) return points;
  let farthest = 0, at = 0;
  for (let index = 1; index < points.length - 1; ++index) {
    const away = distanceToPath(points[index], [points[0], points[points.length - 1]]);
    if (away > farthest) {
      farthest = away;
      at = index;
    }
  }
  if (farthest <= tolerance) return [points[0], points[points.length - 1]];
  return [...thin(points.slice(0, at + 1), tolerance).slice(0, -1), ...thin(points.slice(at), tolerance)];
}

// What a sketch says, for Blip: its area, each label with what it marks,
// and each stroke as a few points, in world x and z.
// sketch: { area, width, height, strokes: [{ colour, points: [[x, y], ...] }],
// labels: [{ text, at: [x, y] }] }, in the picture's pixels. Returns
// { area, labels, strokes }, as lines.
export function describeSketch({ area, width, height, strokes, labels }) {
  const world = ([x, y]) => [area.x0 + x / width * (area.x1 - area.x0), area.z0 + y / height * (area.z1 - area.z0)];
  // Places as precise as a pixel is, give or take.
  const perPixel = Math.abs(area.x1 - area.x0) / width;
  const decimals = Math.max(0, Math.min(3, Math.ceil(-Math.log10(perPixel * 4))));
  const number = (value) => String(+value.toFixed(decimals) || 0);
  const place = (point) => {
    const [x, z] = world(point);
    return `(${number(x)},${number(z)})`;
  };
  // Lines and loops of each colour, numbered when there are several, and
  // its dots together.
  const marks = [];
  for (const [colour] of PENS) {
    const mine = strokes.filter((stroke) => stroke.colour === colour && stroke.points.length);
    const dots = mine.filter((stroke) => pathLength(stroke.points) < DOT_LENGTH);
    const others = mine.filter((stroke) => !dots.includes(stroke)).map((stroke) => {
      const length = pathLength(stroke.points);
      const ends = distance(stroke.points[0], stroke.points[stroke.points.length - 1]);
      return { kind: length >= 3 * LOOP_ENDS && ends <= Math.max(LOOP_ENDS, length * 0.12) ? "loop" : "line",
        points: stroke.points };
    });
    for (const kind of ["line", "loop"]) {
      const ofKind = others.filter((mark) => mark.kind === kind);
      ofKind.forEach((mark, index) => marks.push({ ...mark, name: `${colour} ${kind}${ofKind.length > 1 ? ` ${index + 1}` : ""}`,
        labels: [] }));
    }
    if (dots.length) {
      marks.push({ kind: "dots", name: `${colour} ${dots.length > 1 ? "dots" : "dot"}`, labels: [],
        points: dots.map((stroke) => stroke.points[0]) });
    }
  }
  // Each label names what its dot touches, or the nearest mark within
  // reach; a loop it is inside.
  const labelLines = labels.map((label) => {
    let best = null, nearest = Infinity;
    for (const mark of marks) {
      const within = mark.kind === "loop" && inside(label.at, mark.points);
      const away = within ? 0 : mark.kind === "dots" ? Math.min(...mark.points.map((point) => distance(point, label.at))) :
        distanceToPath(label.at, mark.points);
      if (away < nearest) {
        nearest = away;
        best = { mark, within };
      }
    }
    let line = `${JSON.stringify(label.text)} at ${place(label.at)}`;
    if (best && nearest <= LABEL_REACH) {
      best.mark.labels.push(label.text);
      line += `, ${best.mark.kind === "dots" ? "by" : best.within ? "in" : "on"} the ${best.mark.name}`;
    }
    return line;
  });
  // At most MOST_POINTS points in all, thinned as little as that allows.
  let tolerance = 1, thinned;
  for (;;) {
    thinned = marks.map((mark) => mark.kind === "dots" ? mark.points : thin(mark.points, tolerance));
    if (thinned.reduce((sum, points) => sum + points.length, 0) <= MOST_POINTS || tolerance > 512) break;
    tolerance *= 1.5;
  }
  let left = MOST_POINTS;
  const strokeLines = marks.map((mark, index) => {
    const points = thinned[index].slice(0, Math.max(1, left));
    left -= points.length;
    const named = mark.labels.length ? ` (${mark.labels.map((text) => JSON.stringify(text)).join(", ")})` : "";
    return `${mark.name}${named}: ${points.map(place).join(" ")}`;
  });
  return { area: areaText(area), labels: labelLines, strokes: strokeLines };
}

// An element: el("div", { class: "x", onclick }, child, ...).
function el(tag, attributes = {}, ...children) {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith("on")) element.addEventListener(key.slice(2), value);
    else if (key === "class") element.className = value;
    else if (key === "text") element.textContent = value;
    else element.setAttribute(key, value === true ? "" : value);
  }
  for (const child of children.flat()) if (child !== null && child !== undefined) element.append(child);
  return element;
}

// The sketch pad: the world seen from straight above, which the user draws
// on with four pens and labels with a word or two, then adds to the message
// as a picture (Done). The editor is asked for the picture directly
// (topView), at no cost; strokes and labels are kept in the world's x and
// z, so that zooming out or in keeps them where they were.
// topView({ target, size }): world.js topView. done(picture): the drawing,
// prepared (prepareDrawing), with its description. cancel().
export class SketchPad {
  constructor({ topView, done, cancel, hint = "" }) {
    this.topView = topView;
    this.onDone = done;
    this.onCancel = cancel;
    this.strokes = [];          // { colour, points: [[x, z], ...] }
    this.labels = [];           // { text, at: [x, z] }
    this.history = [];
    this.tool = "red";
    this.view = null;           // the top view: { png, area, width, height, size, target }
    this.background = null;
    this.gesture = null;
    this.editing = null;        // the label being written: { label (null for a new one), at }

    const toolButton = (tool, label, title, colour = null) => el("button", { type: "button", class: "blip-sketch-tool",
      "data-tool": tool, title, "aria-label": title, "aria-pressed": "false", onclick: () => this.setTool(tool) },
    colour ? el("span", { class: "blip-sketch-swatch", style: `background:${colour}` }) : label);
    this.toolButtons = [
      ...PENS.map(([name, colour]) => toolButton(name, "", `Draw in ${name}`, colour)),
      toolButton("eraser", "Eraser", "Rub out a line or a label"),
      toolButton("label", "Label", "Write a word or two where you click, such as trees"),
    ];
    this.canvas = el("canvas", { class: "blip-sketch-canvas", "aria-label": "The world from straight above, to draw on" });
    this.labelInput = el("input", { type: "text", class: "blip-sketch-label-input", maxlength: "40", hidden: true,
      "aria-label": "The label's words", placeholder: "trees, house, lake…" });
    this.removeLabel = el("button", { type: "button", class: "blip-sketch-label-remove", hidden: true,
      title: "Remove the label", "aria-label": "Remove the label" }, "×");
    this.status = el("p", { class: "blip-sketch-status", role: "status" });
    this.zoomOut = el("button", { type: "button", title: "Show more of the world", "aria-label": "Zoom out",
      onclick: () => this.load(this.view ? this.view.size * 2 : null) }, "−");
    this.zoomIn = el("button", { type: "button", title: "Show less of the world, larger", "aria-label": "Zoom in",
      onclick: () => this.load(this.view ? this.view.size / 2 : null) }, "+");
    this.doneButton = el("button", { type: "button", class: "blip-primary", disabled: true, onclick: () => this.done() }, "Done");
    this.element = el("div", { class: "blip-sketch", role: "dialog", "aria-label": "Sketch on the world" },
      hint ? el("p", { class: "blip-sketch-hint", text: hint }) : null,
      el("div", { class: "blip-sketch-tools", role: "toolbar", "aria-label": "Sketch tools" },
        this.toolButtons, el("span", { class: "blip-sketch-gap" }), this.zoomOut, this.zoomIn,
        el("span", { class: "blip-sketch-gap" }),
        el("button", { type: "button", title: "Undo the last change (Ctrl+Z)", onclick: () => this.undo() }, "Undo"),
        el("button", { type: "button", title: "Rub out everything", onclick: () => this.clear() }, "Clear")),
      el("div", { class: "blip-sketch-area" }, this.canvas, this.labelInput, this.removeLabel),
      this.status,
      el("div", { class: "blip-change-buttons" }, this.doneButton,
        el("button", { type: "button", onclick: () => this.onCancel() }, "Cancel")));
    this.setTool("red");

    this.canvas.addEventListener("pointerdown", (event) => this.pointerDown(event));
    this.canvas.addEventListener("pointermove", (event) => this.pointerMove(event));
    this.canvas.addEventListener("pointerup", (event) => this.pointerUp(event));
    this.canvas.addEventListener("pointercancel", () => { this.gesture = null; this.draw(); });
    this.labelInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        this.finishLabel(true);
      } else if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.finishLabel(false);
      }
    });
    this.labelInput.addEventListener("blur", () => setTimeout(() => {
      if (document.activeElement !== this.removeLabel) this.finishLabel(true);
    }, 0));
    // Its × keeps the words' box focused, so that the box is not closed
    // before the click.
    this.removeLabel.addEventListener("pointerdown", (event) => event.preventDefault());
    this.removeLabel.addEventListener("click", () => {
      if (this.editing && this.editing.label) {
        this.remember();
        this.labels = this.labels.filter((label) => label !== this.editing.label);
      }
      this.finishLabel(false);
    });
    this.element.addEventListener("keydown", (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z" && event.target !== this.labelInput) {
        event.preventDefault();
        this.undo();
      } else if (event.key === "Escape" && event.target !== this.labelInput) {
        event.preventDefault();
        event.stopPropagation();
        this.onCancel();
      }
    });
  }

  // Asks the editor for the world from above, size across (as much as its
  // view shows at first), keeping the centre.
  async load(size = null) {
    this.status.textContent = "Looking at the world from above…";
    this.doneButton.disabled = true;
    try {
      const view = await this.topView({ target: this.view ? this.view.target : null, size });
      const background = await createImageBitmap(new Blob([view.png], { type: "image/png" }));
      if (this.background) this.background.close();
      this.background = background;
      this.view = view;
      this.canvas.width = view.width || background.width;
      this.canvas.height = view.height || background.height;
      this.status.textContent = "";
      this.doneButton.disabled = false;
      this.draw();
    } catch (error) {
      this.status.textContent = error.message || String(error);
      this.doneButton.disabled = !this.view;
    }
  }

  close() {
    if (this.background) this.background.close();
    this.background = null;
  }

  setTool(tool) {
    this.tool = tool;
    for (const button of this.toolButtons) button.setAttribute("aria-pressed", String(button.dataset.tool === tool));
    this.canvas.dataset.tool = tool;
  }

  // A world point's place in the picture, and back.
  toPixel([x, z]) {
    const { area } = this.view;
    return [(x - area.x0) / (area.x1 - area.x0) * this.canvas.width, (z - area.z0) / (area.z1 - area.z0) * this.canvas.height];
  }

  toWorld([x, y]) {
    const { area } = this.view;
    return [area.x0 + x / this.canvas.width * (area.x1 - area.x0), area.z0 + y / this.canvas.height * (area.z1 - area.z0)];
  }

  // Where the pointer is, in the picture's pixels.
  pointer(event) {
    const box = this.canvas.getBoundingClientRect();
    return [(event.clientX - box.left) / box.width * this.canvas.width, (event.clientY - box.top) / box.height * this.canvas.height];
  }

  remember() {
    this.history.push(JSON.stringify({ strokes: this.strokes, labels: this.labels }));
    if (this.history.length > 200) this.history.shift();
  }

  undo() {
    const last = this.history.pop();
    if (!last) return;
    ({ strokes: this.strokes, labels: this.labels } = JSON.parse(last));
    this.draw();
  }

  clear() {
    if (!this.strokes.length && !this.labels.length) return;
    this.remember();
    this.strokes = [];
    this.labels = [];
    this.draw();
  }

  // A label's box in the picture: { x, y, width, height }, just above and
  // right of its dot, or turned in from the picture's edges.
  labelBox(label, context = this.canvas.getContext("2d")) {
    context.font = LABEL_FONT;
    const [x, y] = this.toPixel(label.at);
    const width = context.measureText(label.text).width + 12, height = 26;
    let left = x + 8, top = y - 8 - height;
    if (left + width > this.canvas.width) left = x - 8 - width;
    if (top < 0) top = y + 8;
    return { x: left, y: top, width, height };
  }

  labelAt(point) {
    for (let index = this.labels.length - 1; index >= 0; --index) {
      const box = this.labelBox(this.labels[index]);
      if (point[0] >= box.x && point[0] <= box.x + box.width && point[1] >= box.y && point[1] <= box.y + box.height) {
        return this.labels[index];
      }
      if (distance(point, this.toPixel(this.labels[index].at)) <= 8) return this.labels[index];
    }
    return null;
  }

  pointerDown(event) {
    if (!this.view || event.button !== 0) return;
    if (this.editing) this.finishLabel(true);
    const point = this.pointer(event);
    try {
      this.canvas.setPointerCapture(event.pointerId);
    } catch {
      // A pointer the browser no longer has, as after a quick tap.
    }
    const label = this.labelAt(point);
    if (label) {
      this.gesture = { kind: "label", label, from: point, start: label.at, moved: false };
    } else if (this.tool === "label") {
      this.gesture = null;
      this.editLabel(null, this.toWorld(point));
    } else if (this.tool === "eraser") {
      this.remember();
      this.gesture = { kind: "erase", erased: false };
      this.erase(point);
    } else {
      this.remember();
      const stroke = { colour: this.tool, points: [this.toWorld(point)] };
      this.strokes.push(stroke);
      this.gesture = { kind: "pen", stroke, last: point };
      this.draw();
    }
    event.preventDefault();
  }

  pointerMove(event) {
    const gesture = this.gesture;
    if (!gesture) return;
    const point = this.pointer(event);
    if (gesture.kind === "pen") {
      if (distance(point, gesture.last) < 1.5) return;
      gesture.stroke.points.push(this.toWorld(point));
      gesture.last = point;
    } else if (gesture.kind === "erase") {
      this.erase(point);
    } else if (gesture.kind === "label") {
      if (!gesture.moved && distance(point, gesture.from) < 3) return;
      if (!gesture.moved) this.remember();
      gesture.moved = true;
      const start = this.toPixel(gesture.start);
      gesture.label.at = this.toWorld([start[0] + point[0] - gesture.from[0], start[1] + point[1] - gesture.from[1]]);
    }
    this.draw();
  }

  pointerUp() {
    const gesture = this.gesture;
    this.gesture = null;
    if (!gesture) return;
    if (gesture.kind === "erase" && !gesture.erased) this.history.pop();
    if (gesture.kind === "label" && !gesture.moved) this.editLabel(gesture.label, gesture.label.at);
    this.draw();
  }

  // Rubs out the strokes and labels within reach of a point.
  erase(point) {
    const reach = 10;
    const before = this.strokes.length + this.labels.length;
    this.strokes = this.strokes.filter((stroke) => distanceToPath(point, stroke.points.map((at) => this.toPixel(at))) > reach);
    const label = this.labelAt(point);
    if (label) this.labels = this.labels.filter((other) => other !== label);
    if (this.strokes.length + this.labels.length < before) this.gesture.erased = true;
    this.draw();
  }

  // The label's words, in a box over the picture at its spot: a new label
  // (label null), or one clicked to change its words or remove it.
  editLabel(label, at) {
    this.editing = { label, at };
    const [x, y] = this.toPixel(at);
    const box = this.canvas.getBoundingClientRect();
    const scale = box.width / this.canvas.width;
    const left = Math.min(Math.max(0, x * scale - 4), Math.max(0, box.width - 150));
    const top = Math.min(Math.max(0, y * scale - 30), Math.max(0, box.height - 26));
    Object.assign(this.labelInput.style, { left: `${left}px`, top: `${top}px` });
    this.labelInput.value = label ? label.text : "";
    this.labelInput.hidden = false;
    this.removeLabel.hidden = !label;
    Object.assign(this.removeLabel.style, { left: `${left + 124}px`, top: `${top}px` });
    this.labelInput.focus();
    this.draw();
  }

  finishLabel(keep) {
    const editing = this.editing;
    if (!editing) return;
    this.editing = null;
    this.labelInput.hidden = true;
    this.removeLabel.hidden = true;
    const text = this.labelInput.value.replace(/\s+/g, " ").trim().slice(0, 40);
    if (keep && editing.label && text !== editing.label.text) {
      this.remember();
      if (text) editing.label.text = text;
      else this.labels = this.labels.filter((other) => other !== editing.label);
    } else if (keep && !editing.label && text) {
      this.remember();
      this.labels.push({ text, at: editing.at });
    }
    this.draw();
  }

  // The picture with the strokes and labels on it, into a 2D context.
  paint(context) {
    context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (this.background) context.drawImage(this.background, 0, 0, this.canvas.width, this.canvas.height);
    context.lineCap = "round";
    context.lineJoin = "round";
    for (const stroke of this.strokes) {
      const points = stroke.points.map((at) => this.toPixel(at));
      const colour = (PENS.find(([name]) => name === stroke.colour) || PENS[0])[1];
      // A dark edge, so that it shows on any ground.
      for (const [style, width] of [["rgba(0, 0, 0, 0.55)", PEN_WIDTH + 3], [colour, PEN_WIDTH]]) {
        context.strokeStyle = style;
        context.fillStyle = style;
        context.lineWidth = width;
        if (points.length === 1 || pathLength(points) < DOT_LENGTH) {
          context.beginPath();
          context.arc(points[0][0], points[0][1], width, 0, Math.PI * 2);
          context.fill();
        } else {
          context.beginPath();
          context.moveTo(points[0][0], points[0][1]);
          for (const point of points.slice(1)) context.lineTo(point[0], point[1]);
          context.stroke();
        }
      }
    }
    for (const label of this.labels) {
      if (this.editing && this.editing.label === label) continue;
      const [x, y] = this.toPixel(label.at);
      const box = this.labelBox(label, context);
      context.fillStyle = "#111";
      context.beginPath();
      context.arc(x, y, 5, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#fff";
      context.beginPath();
      context.arc(x, y, 2.5, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "rgba(255, 255, 255, 0.92)";
      context.strokeStyle = "#111";
      context.lineWidth = 2;
      context.fillRect(box.x, box.y, box.width, box.height);
      context.strokeRect(box.x, box.y, box.width, box.height);
      context.fillStyle = "#111";
      context.font = LABEL_FONT;
      context.textBaseline = "middle";
      context.fillText(label.text, box.x + 6, box.y + box.height / 2 + 1);
    }
  }

  draw() {
    if (!this.view) return;
    this.paint(this.canvas.getContext("2d"));
  }

  // The drawing as a picture, with what it says in the world's x and z.
  async done() {
    if (!this.view) return;
    if (this.editing) this.finishLabel(true);
    this.doneButton.disabled = true;
    try {
      const canvas = new OffscreenCanvas(this.canvas.width, this.canvas.height);
      this.paint(canvas.getContext("2d"));
      const sketch = describeSketch({ area: this.view.area, width: this.canvas.width, height: this.canvas.height,
        strokes: this.strokes.map((stroke) => ({ colour: stroke.colour, points: stroke.points.map((at) => this.toPixel(at)) })),
        labels: this.labels.map((label) => ({ text: label.text, at: this.toPixel(label.at) })) });
      this.onDone(await prepareDrawing(canvas, "sketch", sketch));
    } catch (error) {
      this.status.textContent = error.message || String(error);
      this.doneButton.disabled = false;
    }
  }
}
